import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { SIDEBAR_GRAPH_WIDTH } from "../../../constants/constants";
import {
  TREND_CURVE_CHART_HEIGHT,
  TREND_CURVE_EXPANDED_CHART_HEIGHT,
  TREND_CURVE_EXPANDED_CHART_WIDTH,
  TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT,
  TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED,
} from "../../../constants/trendCurveConstants";
import { TrendCurveChartData } from "../../../interface/DesignerInterface";
import { computeTrendCurvePreferredChartWidth } from "../../../utils/trendSidebarGraphUtils/trendCurveChartHelpers";
import { renderTrendCurveChart } from "../../../utils/trendSidebarGraphUtils/trendCurveChartRenderer";
import TrendCurveLegend from "./TrendCurveLegend";

type TrendCurvesProps = {
  trendCurveData: TrendCurveChartData;
  width?: number;
  trendsCurveExpandedView: boolean;
  /** Fill parent width (expanded view) instead of a fixed pixel width. */
  fillContainer?: boolean;
  /** Show contribution % labels above points (typically when one trend is solo). */
  showContributionLabels?: boolean;
  /** Colour + name legend box beside the chart (included in screenshots). */
  showLegend?: boolean;
};

const TrendCurvesGraph = forwardRef<SVGSVGElement | null, TrendCurvesProps>(
  (
    {
      trendCurveData,
      width = SIDEBAR_GRAPH_WIDTH,
      trendsCurveExpandedView,
      fillContainer = false,
      showContributionLabels = false,
      showLegend = true,
    },
    ref
  ) => {
    const chartRef = useRef<SVGSVGElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [measuredWidth, setMeasuredWidth] = useState(width);

    useImperativeHandle(ref, () => chartRef.current as SVGSVGElement);

    // Measure before paint so remounts / fillContainer don't flash SIDEBAR_GRAPH_WIDTH.
    useLayoutEffect(() => {
      if (!fillContainer) {
        setMeasuredWidth(width);
        return;
      }

      const node = rootRef.current;
      if (!node) return;

      const update = () => {
        const next = Math.max(width, Math.floor(node.clientWidth));
        if (next > 0) {
          setMeasuredWidth(prev => (prev === next ? prev : next));
        }
      };

      update();
      const observer = new ResizeObserver(update);
      observer.observe(node);
      return () => observer.disconnect();
    }, [fillContainer, width]);

    const effectiveWidth = fillContainer ? measuredWidth : width;
    const chartHeight = trendsCurveExpandedView
      ? TREND_CURVE_EXPANDED_CHART_HEIGHT
      : TREND_CURVE_CHART_HEIGHT;
    const legendWidth = trendsCurveExpandedView
      ? TREND_CURVE_SIDE_LEGEND_WIDTH_EXPANDED
      : TREND_CURVE_SIDE_LEGEND_WIDTH_COMPACT;
    const gap = showLegend ? (trendsCurveExpandedView ? 4 : 12) : 0;
    const availableChartWidth = showLegend
      ? Math.max(260, effectiveWidth - legendWidth - gap)
      : effectiveWidth;
    const preferredChartWidth = computeTrendCurvePreferredChartWidth(
      trendCurveData.labels.length,
      trendsCurveExpandedView,
      availableChartWidth
    );
    // Sidebar charts fill the whole sidebar width (min clamped); expanded keeps
    // content-based sizing so sparse charts stay proportional.
    const chartWidth =
      fillContainer && !trendsCurveExpandedView
        ? availableChartWidth
        : preferredChartWidth;

    useEffect(() => {
      if (!chartRef.current || !tooltipRef.current || !containerRef.current) {
        return;
      }
      if (chartWidth < 100) return;

      renderTrendCurveChart({
        svgElement: chartRef.current,
        trendCurveData,
        width: chartWidth,
        height: chartHeight,
        expanded: trendsCurveExpandedView,
        interactive: true,
        showContributionLabels,
        legendPlacement: "none",
        tooltipElement: tooltipRef.current,
        containerElement: containerRef.current,
      });
    }, [
      trendCurveData,
      chartWidth,
      trendsCurveExpandedView,
      chartHeight,
      showContributionLabels,
    ]);

    return (
      <div
        ref={rootRef}
        className="flex w-full items-stretch"
        style={{ minHeight: chartHeight, gap }}
      >
        <div
          className={`relative flex min-w-0 flex-1 overflow-visible ${
            trendsCurveExpandedView ? "justify-center" : "justify-start"
          }`}
          style={{ height: chartHeight }}
        >
          {/* Inner box matches SVG width so tooltips stay aligned with the chart. */}
          <div
            ref={containerRef}
            className="relative overflow-visible"
            style={{ width: chartWidth, height: chartHeight }}
          >
            <svg
              ref={chartRef}
              id="trend-curves-chart"
              width={chartWidth}
              height={chartHeight}
              className="overflow-visible"
            />
            <div
              ref={tooltipRef}
              className="pointer-events-none absolute z-10"
              style={{ visibility: "hidden" }}
            />
          </div>
        </div>
        {showLegend ? (
          <TrendCurveLegend
            datasets={trendCurveData.datasets}
            expanded={trendsCurveExpandedView}
            width={legendWidth}
          />
        ) : null}
      </div>
    );
  }
);

TrendCurvesGraph.displayName = "TrendCurvesGraph";

export default TrendCurvesGraph;
export { TREND_CURVE_EXPANDED_CHART_WIDTH };
